import React, { useState, useEffect } from "react";
import './Sessions.css';
import axios from "axios";
import api from '../../utils/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import CenteredLoading from '../../components/CenteredLoading';
import { FaRegCommentDots, FaChartLine } from 'react-icons/fa';
import { BsMusicNote, BsCalendar, BsClock, BsStarFill, BsStar, BsTrash, BsMic, BsInfoCircle, BsPencil, BsSearch } from 'react-icons/bs';
import { BsList } from 'react-icons/bs';
import { FiEdit3 } from 'react-icons/fi';

const Sessions = () => {
    useEffect(() => {
        document.title = "SwavAI - Sessions";
    }, []);

    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [showImproved, setShowImproved] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [originalTranscript, setOriginalTranscript] = useState('');
    const [improvedTranscript, setImprovedTranscript] = useState('');
    const [sessionIdFromQuery, setSessionIdFromQuery] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [feedbackRatings, setFeedbackRatings] = useState({});
    const [favourites, setFavourites] = useState({});
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [editingTitle, setEditingTitle] = useState(null);
    const [newTitle, setNewTitle] = useState("");
    const [mobileListVisible, setMobileListVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoadingImproved, setIsLoadingImproved] = useState(false);

    // Add click handler for score cards
    const handleSkillClick = (skill) => {
        setSelectedSkill(skill);
    };

    const filteredSessions = sessions.filter(session =>
        session.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.session_id.toString().includes(searchTerm)
    );

    const handleDelete = async (sessionId, e) => {
        e.stopPropagation(); // Prevent session selection when clicking delete
        if (window.confirm('Are you sure you want to delete this session?')) {
            try {
                await api.delete(`/session/delete/${sessionId}`);
                setSessions(sessions.filter(session => session.session_id !== sessionId));
                if (selectedSession?.session_id === sessionId) {
                    setSelectedSession(null);
                }
            } catch (error) {
                console.error('Error deleting session:', error);
                alert('Failed to delete session');
            }
        }
    };

    const handleFavourite = async (sessionId, e) => {
        e.stopPropagation(); // Prevent session selection when clicking favourite
        try {
            const newFavouriteStatus = !favourites[sessionId];
            await api.put(`/session/favourite/${sessionId}`, {
                favourite: newFavouriteStatus
            });

            // Update local favourite status
            setFavourites(prev => ({
                ...prev,
                [sessionId]: newFavouriteStatus
            }));

            // Update session's favourite status
            const updatedSessions = sessions.map(session =>
                session.session_id === sessionId
                    ? { ...session, favourite: newFavouriteStatus }
                    : session
            );

            // Re-sort sessions - favourites first, then by date
            const sortedSessions = [...updatedSessions].sort((a, b) => {
                if (a.favourite === b.favourite) {
                    return new Date(b.upload_date) - new Date(a.upload_date);
                }
                return b.favourite ? 1 : -1;
            });

            setSessions(sortedSessions);
        } catch (error) {
            console.error('Error updating favourite status:', error);
            alert('Failed to update favourite status');
        }
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const sessionId = queryParams.get('session');
        setSessionIdFromQuery(sessionId);
    }, [location.search]);

    useEffect(() => {
        const fetchSessions = async () => {
            try {
                const response = await api.get("/session/all");
                const sortedSessions = response.data.sort((a, b) => {
                    if (a.favourite === b.favourite) {
                        return new Date(b.upload_date) - new Date(a.upload_date);
                    }
                    return b.favourite ? 1 : -1;
                });
                setSessions(sortedSessions);
                const favouriteStatus = {};
                sortedSessions.forEach(session => {
                    favouriteStatus[session.session_id] = session.favourite;
                });
                setFavourites(favouriteStatus);
                setLoading(false);

                // Determine which session to select
                let sessionToSelect;

                if (sessionIdFromQuery) {
                    // Try to find session by ID from query parameter
                    sessionToSelect = sortedSessions.find(
                        session => session.session_id === parseInt(sessionIdFromQuery)
                    );
                }

                if (!sessionToSelect && sortedSessions.length > 0) {
                    // If no session ID from query or not found, select the latest session by upload_date
                    sessionToSelect = sortedSessions.reduce((latest, session) => {
                        return new Date(session.upload_date) > new Date(latest.upload_date) ? session : latest;
                    }, sortedSessions[0]);
                }

                setSelectedSession(sessionToSelect);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching sessions:", err);
                setError("Failed to fetch sessions.");
                setLoading(false);

                // Handle authentication errors
                if (err.response?.status === 401) {
                    navigate('/login');
                }
            }
        };

        fetchSessions();
    }, [navigate, sessionIdFromQuery]);

    useEffect(() => {
        if (selectedSession) {
            // Set transcript to the original transcript of selected session
            setTranscript(selectedSession.transcription);
            setOriginalTranscript(selectedSession.transcription);
            setImprovedTranscript('');
            setShowImproved(false);
        }
    }, [selectedSession]);

    const toggleTranscript = async () => {
        if (!showImproved && selectedSession) {
            if (!improvedTranscript) {
                setIsLoadingImproved(true);
                try {
                    const response = await api.get(`/recording/improved-transcript/${selectedSession.id}`);
                    setImprovedTranscript(response.data.improved_transcript);
                    setTranscript(response.data.improved_transcript);
                    setShowImproved(true);
                } catch (error) {
                    console.error('Error fetching improved transcript:', error);
                } finally {
                    setIsLoadingImproved(false);
                }
            } else {
                setTranscript(improvedTranscript);
                setShowImproved(true);
            }
        } else {
            setTranscript(originalTranscript);
            setShowImproved(false);
        }
    };

    if (loading) {
        return <CenteredLoading />; // Display the Loading component while loading
    }

    const ScoreCircle = ({ score }) => {
        const circumference = 2 * Math.PI * 36;
        const progressOffset = circumference * (1 - Math.abs(score) / 100);
        const strokeColor = score > 0
            ? `rgba(100, 255, 100, ${score / 100})`
            : score < 0
                ? `rgba(255, 0, 0, ${Math.abs(score) / 100})`
                : '#ddd';

        return (
            <div className="score-circle">
                <svg viewBox="0 0 80 80">
                    <circle className="background" cx="40" cy="40" r="36" />
                    <circle
                        className="progress"
                        cx="40"
                        cy="40"
                        r="36"
                        stroke={strokeColor}
                        strokeDasharray={circumference}
                        strokeDashoffset={progressOffset}
                    />
                </svg>
                <div className="score-value">{score > 0 ? `+${score}` : score}</div>
            </div>
        );
    };

    const getAudioUrl = (s3Key) => {
        if (!s3Key) return null;
        return `https://${process.env.REACT_APP_S3_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${s3Key}`;
    };

    const handleRateFeedback = async (sessionId, rating) => {
        try {
            await api.post('/session/rate-feedback', {
                sessionId,
                rating: rating ? 'positive' : 'negative'
            });
            setFeedbackRatings(prev => ({
                ...prev,
                [sessionId]: rating
            }));
        } catch (error) {
            console.error('Error rating feedback:', error);
            alert('Failed to rate feedback');
        }
    };

    const formatDuration = (duration) => {
        const seconds = Math.floor(duration % 60);
        const minutes = Math.floor((duration / 60) % 60);
        const hours = Math.floor(duration / 3600);
        return `${hours > 0 ? `${hours}h ` : ""}${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`;
    };

    const handleTitleEdit = (sessionId, title) => {
        setEditingTitle(sessionId);
        setNewTitle(title);
    };

    const handleTitleChange = (e) => {
        setNewTitle(e.target.value);
    };

    const saveTitle = async (sessionId) => {
        try {
            await api.put(`/session/update-title/${sessionId}`, { title: newTitle });
            setSessions(sessions.map(session =>
                session.session_id === sessionId ? { ...session, title: newTitle } : session
            ));
            setEditingTitle(null);
        } catch (error) {
            console.error('Error updating title:', error);
            alert('Failed to update title');
        }
    };

    if (loading) {
        return <p>Loading sessions...</p>;
    }

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <div className="sessions">
            <button
                className="mobile-toggle"
                onClick={() => setMobileListVisible(!mobileListVisible)}
            >
                <BsList size={24} />
            </button>
            <div className={`session-list ${mobileListVisible ? 'visible' : ''}`}>
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Search sessions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                {filteredSessions.length === 0 ? (
                    <div className="empty-state">
                        {searchTerm ? (
                            <>
                                <BsSearch className="empty-state-icon" />
                                <p className="empty-state-text">No sessions found</p>
                            </>
                        ) : (
                            <>
                                <BsMusicNote className="empty-state-icon" />
                                <p className="empty-state-text">No recorded sessions yet</p>
                                <button className="empty-state-action" onClick={() => navigate('/')}>
                                    <BsMic /> Start Recording
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    filteredSessions.map((session) => (
                        <div
                            key={session.session_id}
                            className={`session-item ${selectedSession?.session_id === session.session_id ? 'selected' : ''} ${favourites[session.session_id] ? 'favourite' : ''}`}
                            onClick={() => setSelectedSession(session)}
                        >
                            <div className="session-header">
                                <h4 className="session-title">
                                    {session.title ? session.title : 'Untitled Session'}
                                </h4>
                                <div className="session-actions">
                                    <button
                                        className={`favourite-btn ${favourites[session.session_id] ? 'active' : ''}`}
                                        onClick={(e) => handleFavourite(session.session_id, e)}
                                        title="Favorite"
                                    >
                                        {favourites[session.session_id] ? <BsStarFill /> : <BsStar />}
                                    </button>
                                    <button
                                        className="delete-btn"
                                        onClick={(e) => handleDelete(session.session_id, e)}
                                        title="Delete"
                                    >
                                        <BsTrash />
                                    </button>
                                </div>
                            </div>
                            <div className="session-meta">
                                <span className="meta-item">
                                    <BsCalendar /> {new Date(session.upload_date).toLocaleDateString()}
                                </span>
                                <span className="meta-item">
                                    <BsClock /> {new Date(session.upload_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="meta-item">
                                    <BsMic /> {formatDuration(session.duration)}
                                </span>
                                <span className="meta-item session-id">
                                    <BsInfoCircle /> Type: {(session.feedback_type || 'General').split(' ').map(word =>
                                        word.charAt(0).toUpperCase() + word.slice(1)
                                    ).join(' ')}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div className="session-detail">
                {selectedSession ? (
                    <>
                        <div className="detail-header">
                            {editingTitle === selectedSession.session_id ? (
                                <div className="edit-title-container">
                                    <input
                                        type="text"
                                        value={newTitle}
                                        onChange={handleTitleChange}
                                        onBlur={() => saveTitle(selectedSession.session_id)}
                                        onKeyDown={(e) => e.key === 'Enter' && saveTitle(selectedSession.session_id)}
                                        className="session-title-input"
                                        autoFocus
                                    />
                                </div>
                            ) : (
                                <h2 className="detail-title">
                                    {selectedSession.title || 'Untitled Session'}
                                    <button
                                        className="edit-btn"
                                        onClick={() => handleTitleEdit(selectedSession.session_id, selectedSession.title)}
                                    >
                                        <FiEdit3 />
                                    </button>
                                </h2>
                            )}
                        </div>

                        <div className="audio-player-container">
                            <audio
                                key={selectedSession.session_id}
                                controls
                                className="audio-player"
                                onError={(e) => console.error("Audio loading error:", e)}
                            >
                                <source
                                    src={getAudioUrl(selectedSession.s3_key)}
                                    type="audio/wav"
                                />
                                Your browser does not support the audio element.
                            </audio>
                        </div>

                        <div className="scores-container">
                            <div className="scores-header">
                                <FaChartLine />
                                <h3>Performance Scores</h3>
                            </div>
                            <p>Click on a score to view detailed feedback</p>
                            <div className="session-scores">
                                {["tone", "confidence", "clarity", "efficiency", "pace"].map(skill => (
                                    <div
                                        key={skill}
                                        className="score-card"
                                        onClick={() => handleSkillClick(skill)}
                                        title={`Click to view ${skill} details`}
                                    >
                                        <ScoreCircle score={selectedSession[`${skill}_score`]} />
                                        <span className="score-label">
                                            {skill.charAt(0).toUpperCase() + skill.slice(1)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {selectedSkill && (
                            <div className="skill-feedback-modal">
                                <div className="modal-content">
                                    <h4>{selectedSkill.charAt(0).toUpperCase() + selectedSkill.slice(1)} Feedback</h4>
                                    <p>{selectedSession[`${selectedSkill}_feedback`]}</p>
                                    <button onClick={() => setSelectedSkill(null)}>Close</button>
                                </div>
                            </div>
                        )}
                        <div className="transcript-section">
                            <div className="transcript-header">
                                <h3>Transcript</h3>
                                <div className="transcript-tabs">
                                    <button
                                        className={`tab-btn ${!showImproved ? 'active' : ''}`}
                                        onClick={() => toggleTranscript()}
                                    >
                                        Original
                                    </button>
                                    <button
                                        className={`tab-btn ${showImproved ? 'active' : ''}`}
                                        onClick={() => toggleTranscript()}
                                        disabled={isLoadingImproved}
                                    >
                                        {isLoadingImproved ? 'Loading...' : 'Improved'}
                                    </button>
                                </div>
                            </div>
                            <div className="transcript-content">
                                {isLoadingImproved ? (
                                    <div className="transcript-loading">
                                        <CenteredLoading />
                                    </div>
                                ) : (
                                    <>
                                        <div className="transcript-badge">
                                            {showImproved ? 'AI Improved Version' : 'Original Recording'}
                                        </div>
                                        <p className="transcript-text">{transcript}</p>
                                    </>
                                )}
                            </div>
                        </div>
                        {selectedSession && (
                            <div className="message-feedback">
                                <h3>
                                    <FaRegCommentDots />
                                    Feedback
                                </h3>
                                <p>{selectedSession.feedback}</p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="empty-state">
                        <BsInfoCircle className="empty-state-icon" />
                        <p className="empty-state-text">Select a session from the list to view its details</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Sessions;